/**
 * CategoryReconciliationBanner.tsx — what the per-category check concluded.
 *
 * WHY THIS EXISTS, AND WHY IT IS NOT THE BANNER ABOVE IT
 * -----------------------------------------------------
 * `ReconciliationBanner` answers "does the money add up?". This answers "does it
 * add up in the right places?" — and the second question is not implied by the
 * first. On TORINO the total reconciles to the cent while € 63.371,00 sits under
 * `Opere in carpenteria` instead of the uncategorised bucket: two errors that
 * cancel in the sum. A user reading only the green "Totali verificati" above
 * would see nothing wrong with a breakdown that is wrong.
 *
 * That is precisely why this file exists at all. The check itself was built,
 * gated and correct, and its verdict reached `Job.result` and no screen — the
 * same shape as the rule that lived in three documents and no code, and the same
 * shape the banner above was built to fix one level up. The independent QA pass
 * found it here again, in the review of the very feature meant to end it.
 *
 * THE DESIGN RULE, INHERITED: ABSENCE MUST NEVER READ AS A PASS.
 * `not_comparable` is the CORRECT and expected outcome on a document like AOSTA,
 * whose recap tables each describe one sub-computo. It is rendered muted rather
 * than as a warning — it is information, not a fault — but it is rendered, since
 * silence would be indistinguishable from "checked, agrees".
 */
import { type AnalysisJobStatus, type CategoryReconciliationVerdict } from "../../lib/tenderApi";
import { fmtEurCents } from "../../lib/format";

type Tone = "ok" | "warn" | "err" | "muted";

const TONE_STYLE: Record<Tone, { fg: string; bg: string; border: string }> = {
  ok: { fg: "var(--c-ok)", bg: "var(--c-ok-soft)", border: "rgba(47,125,78,0.30)" },
  warn: { fg: "var(--c-warn)", bg: "var(--c-warn-soft)", border: "rgba(184,115,12,0.32)" },
  err: { fg: "var(--c-err)", bg: "var(--c-err-soft)", border: "rgba(181,61,42,0.34)" },
  muted: { fg: "var(--c-ink-3)", bg: "var(--c-bg-elev)", border: "var(--c-line)" },
};

interface Copy {
  tone: Tone;
  title: string;
  detail: string;
}

export function categoryReconciliationCopy(
  verdict: CategoryReconciliationVerdict | null,
  jobStatus?: AnalysisJobStatus | null,
): Copy | null {
  if (verdict === null) {
    // Three reasons a verdict can be absent, and only one of them is "this
    // analysis predates the check" — see the sibling banner for the full case.
    if (jobStatus === "queued" || jobStatus === "running") {
      return {
        tone: "warn",
        title: "Analisi in corso — ripartizione non riverificata",
        detail:
          "I valori mostrati provengono dall'analisi precedente e non sono stati riverificati.",
      };
    }
    if (jobStatus === "failed") {
      return {
        tone: "warn",
        title: "Ultima analisi non riuscita — ripartizione non riverificata",
        detail: "I valori mostrati provengono da un'analisi precedente.",
      };
    }
    return {
      tone: "muted",
      title: "Ripartizione per categoria non verificata",
      detail:
        "Questa analisi è precedente al controllo della ripartizione per categoria. " +
        "Per verificarla, esegui di nuovo l'analisi.",
    };
  }

  const axis = verdict.axis ? `«${verdict.axis}»` : "categoria";
  // The verdict is a SNAPSHOT of the extraction, not of the tree below it.
  // Re-filing line items is what this page is for, and doing so updates the
  // category totals live while this banner keeps asserting the extracted figure
  // — two contradictory numbers for one category on one screen. Saying which
  // moment the banner describes is the honest minimum; making the alarm
  // clearable by re-filing would mean re-deriving it from the current tree,
  // which is a larger change than this one.
  const snapshot =
    " Riferito al momento dell'estrazione: ricategorizzare le voci non aggiorna questo controllo.";

  switch (verdict.status) {
    case "ok":
      // `explanation` is normally absent on `ok`. When present it names a recap
      // page we RECOGNISED as not being a category breakdown (a safety-cost
      // summary) and therefore set aside. It is shown rather than dropped —
      // the reader is told what was excluded and why — but it does not turn the
      // banner amber, because coverage is not partial and a false warning is
      // how a true one stops being read.
      return {
        tone: "ok",
        title: "Ripartizione per categoria verificata",
        detail:
          `Ogni categoria ${axis} corrisponde alla ripartizione dichiarata dal computo ` +
          `(${verdict.comparisons.length} voci confrontate).` +
          (verdict.explanation ? ` ${verdict.explanation}` : "") +
          snapshot,
      };

    case "partial":
      // Every compared category agreed — but a page carried a recap heading we
      // could not read, so "verificata" would overstate the coverage.
      return {
        tone: "warn",
        title: "Ripartizione verificata solo in parte",
        detail:
          (verdict.explanation ??
            "Alcune tabelle di RIEPILOGO non sono state riconosciute: la verifica non copre " +
              "l'intero documento.") + snapshot,
      };

    case "mismatch": {
      const bad = verdict.comparisons.filter((c) => c.status === "mismatch");
      // The count is only shown when we actually have the comparisons to count.
      // The server's `explanation` carries its own "N su M", so a defensive
      // empty array rendered a title saying "(0 su 0)" beside a body saying
      // "2 categorie su 7" — one verdict contradicting itself on screen.
      const count =
        verdict.comparisons.length > 0 ? ` (${bad.length} su ${verdict.comparisons.length})` : "";
      return {
        tone: "err",
        title: `Ripartizione per categoria non corrispondente${count}`,
        detail:
          (verdict.explanation ??
            `Alcune categorie non coincidono con la ripartizione dichiarata dal computo.`) +
          snapshot,
      };
    }

    case "unread_amounts": {
      // Goal 55A, last round (Omar, 4 Oct): no category disagrees, but some hold an amount
      // that was not read — their sum is unknown, so they are not verifiable (never «non
      // corrispondente», which read the blank cell as € 0,00).
      const k = verdict.unread_categories ?? 0;
      return {
        tone: "warn",
        title: "Ripartizione per categoria non verificabile",
        detail:
          (verdict.explanation ??
            `${k === 1 ? "Una categoria contiene" : `${k} categorie contengono`} importi non ` +
              `letti: la ripartizione non è verificabile.`) + snapshot,
      };
    }

    case "axis_not_extracted":
      return {
        tone: "warn",
        title: "Ripartizione per categoria non confrontabile",
        detail:
          verdict.explanation ??
          "Il computo dichiara una ripartizione che l'estrazione non ha prodotto.",
      };

    case "not_comparable":
      // Muted, not a warning: on a master computo whose recap tables each cover
      // one sub-computo, declining IS the correct answer, and colouring it amber
      // on every such tender would train people to ignore the colour.
      return {
        tone: "muted",
        title: "Ripartizione per categoria non verificabile",
        detail:
          verdict.explanation ??
          "Le tabelle di RIEPILOGO del computo non consentono un confronto per categoria.",
      };

    case "unreadable":
      // NOT muted. "We read the document and it offers nothing comparable" is a
      // normal outcome; "we could not read the document at all" is a fault, and
      // the grand-total sibling already renders it amber. One colour for both
      // would let a crash borrow the calm of a principled decline.
      return {
        tone: "warn",
        title: "Controllo della ripartizione non eseguito",
        detail:
          verdict.explanation ??
          "Non è stato possibile rileggere il computo per verificare la ripartizione.",
      };

    default:
      return {
        tone: "warn",
        title: "Esito del controllo per categoria sconosciuto",
        detail:
          `Il controllo ha restituito lo stato "${verdict.status}", non riconosciuto da ` +
          `questa versione dell'applicazione.`,
      };
  }
}

interface Props {
  verdict: CategoryReconciliationVerdict | null;
  /** False while the analysis has produced no line items — an un-analysed
   * project shows nothing, rather than "not verified", which reads as a fault. */
  hasAnalysis: boolean;
  /** Status of the job the verdict was read from — see `categoryReconciliationCopy`. */
  jobStatus?: AnalysisJobStatus | null;
}

export function CategoryReconciliationBanner({
  verdict,
  hasAnalysis,
  jobStatus,
}: Props): JSX.Element | null {
  if (!hasAnalysis) return null;
  const copy = categoryReconciliationCopy(verdict, jobStatus);
  if (!copy) return null;
  const tone = TONE_STYLE[copy.tone];
  const bad = (verdict?.comparisons ?? []).filter((c) => c.status === "mismatch");

  return (
    <div
      role={copy.tone === "err" ? "alert" : "status"}
      data-testid="category-reconciliation-banner"
      data-tone={copy.tone}
      style={{
        padding: "11px 14px",
        marginBottom: 16,
        borderRadius: 8,
        background: tone.bg,
        border: `1px solid ${tone.border}`,
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 600, color: tone.fg }}>{copy.title}</div>
      <div
        style={{
          fontSize: 12.5,
          marginTop: 3,
          color: "var(--c-ink-2)",
          lineHeight: 1.5,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {copy.detail}
      </div>

      {bad.length > 0 && (
        /* The offending categories by name and amount. The banner above can only
           say "something is wrong somewhere"; the whole value of the per-category
           check is that it says WHERE, so the list is part of the verdict rather
           than a detail behind a disclosure. */
        <div style={{ marginTop: 8, overflowX: "auto" }}>
          <table
            style={{
              borderCollapse: "collapse",
              fontSize: 12,
              fontVariantNumeric: "tabular-nums",
              minWidth: 380,
            }}
          >
            <thead>
              <tr style={{ color: "var(--c-ink-3)", textAlign: "left" }}>
                <th style={{ padding: "2px 12px 2px 0", fontWeight: 500 }}>Categoria</th>
                <th style={{ padding: "2px 12px 2px 0", fontWeight: 500, textAlign: "right" }}>
                  Dichiarato
                </th>
                <th style={{ padding: "2px 12px 2px 0", fontWeight: 500, textAlign: "right" }}>
                  Estratto
                </th>
                <th style={{ padding: "2px 0", fontWeight: 500, textAlign: "right" }}>
                  Differenza
                </th>
              </tr>
            </thead>
            <tbody>
              {bad.map((c) => (
                <tr key={`${c.code ?? "?"}-${c.name}`} data-testid="category-mismatch-row">
                  <td style={{ padding: "2px 12px 2px 0", color: "var(--c-ink-2)" }}>
                    {c.code ? `${c.code} ` : ""}
                    {c.name}
                  </td>
                  <td style={{ padding: "2px 12px 2px 0", textAlign: "right" }}>
                    {fmtEurCents(c.stated)}
                  </td>
                  <td style={{ padding: "2px 12px 2px 0", textAlign: "right" }}>
                    {c.extracted === null ? "—" : fmtEurCents(c.extracted)}
                  </td>
                  <td style={{ padding: "2px 0", textAlign: "right", color: tone.fg }}>
                    {/* Sign OUTSIDE the currency, so +/- line up in the column.
                        Formatting the raw negative yields `€ -63.371,00`, whose
                        minus is buried after the symbol and does not align with
                        the `+` above it — and the mirrored pair of signs is the
                        one thing that shows money was moved, not lost. */}
                    {c.delta === null
                      ? "—"
                      : `${c.delta < 0 ? "-" : "+"}${fmtEurCents(Math.abs(c.delta))}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
